import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { bankService } from '@/services/bankService'
import { contractService } from '@/services/contractService'
import { Bank, EmployeeOption } from '@/api/types'

/**
 * Bank Detail/Form Page
 * Form for creating new banks or editing existing ones
 */
export const BanksDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId
  const listEmpId = useMemo(() => {
    const n = Number(searchParams.get('empId') ?? searchParams.get('EmpId') ?? 0)
    return n > 0 && !Number.isNaN(n) ? n : 0
  }, [searchParams])
  const backToList = useCallback(() => {
    navigate(listEmpId ? `/banks?empId=${listEmpId}` : '/banks')
  }, [navigate, listEmpId])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [bank, setBank] = useState<Bank>({
    id: 0,
    empId: 0,
    bankName: '',
    accountTitle: '',
    accountNo: '',
    iban: '',
    swiftCode: '',
    note: '',
    branchId: branchId || 0,
  })

  // Lookup data
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const selectedEmployee = useMemo(
    () => (bank.empId ? employees.find((e) => e.empId === bank.empId) : undefined),
    [bank.empId, employees]
  )

  // Load lookup data
  useEffect(() => {
    if (!branchId) {
      console.warn('🔵 [BankDetail] BranchId not available, cannot load employees')
      console.warn('User object:', user)
      console.warn('Selected branch:', user?.selectedBranch)
      return
    }

    const loadLookups = async () => {
      try {
        setLoading(true)
        console.log('🔵 [BankDetail] Loading employees for branchId:', branchId)
        const employeesData = await contractService.getAllEmployees(branchId)
        console.log('🔵 [BankDetail] Employees data received:', employeesData?.length || 0, 'employees')
        console.log('🔵 [BankDetail] Employees data sample:', employeesData?.[0])
        console.log('🔵 [BankDetail] All employees:', employeesData)
        
        if (employeesData && employeesData.length > 0) {
          console.log('✅ [BankDetail] Setting employees state with', employeesData.length, 'employees')
          setEmployees(employeesData)
        } else {
          console.warn('⚠️ [BankDetail] No employees data to set, employeesData:', employeesData)
          setEmployees([])
        }
      } catch (error: any) {
        console.error('❌ [BankDetail] Failed to load lookups', error)
        console.error('Error message:', error.message)
        console.error('Error response:', error.response?.data)
        console.error('Error status:', error.response?.status)
        console.error('Error URL:', error.config?.url)
        toast.error(error.response?.data?.message || 'Failed to load lookup data')
        setEmployees([])
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId, user])

  useEffect(() => {
    if (isEditMode) return
    if (!listEmpId) return
    setBank((prev) => (prev.empId === listEmpId ? prev : { ...prev, empId: listEmpId }))
  }, [isEditMode, listEmpId])

  // Load bank data in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return

    const loadBank = async () => {
      try {
        setLoading(true)
        const bankData = await bankService.getBankById(Number(id))
        
        setBank({
          ...bankData,
          branchId: branchId || bankData.branchId,
        })
      } catch (error: any) {
        console.error('Failed to load bank', error)
        toast.error(error.response?.data?.message || 'Failed to load bank data')
        backToList()
      } finally {
        setLoading(false)
      }
    }

    loadBank()
  }, [isEditMode, id, navigate, branchId])

  // Prepare options for employee dropdown
  const employeeOptions: SearchableSelectOption[] = useMemo(() => {
    console.log('🔵 [BankDetail] Preparing employee options from:', employees.length, 'employees')
    if (employees.length === 0) {
      console.warn('⚠️ [BankDetail] No employees available for dropdown')
    } else {
      console.log('🔵 [BankDetail] First employee sample:', employees[0])
    }
    const options = employees.map((emp) => {
      // Use fullName if available, otherwise construct from firstName, secondName, lastName
      const fullName = emp.fullName || 
        [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn]
          .filter(Boolean)
          .join(' ')
          .trim() || 
        `Employee ${emp.empId}`
      return {
        value: String(emp.empId),
        label: (emp.empCode ? `${emp.empCode} — ${fullName}` : fullName).trim(),
      }
    })
    console.log('✅ [BankDetail] Created employee options:', options.length)
    return options
  }, [employees])
  
  // Debug: Log employees state changes
  useEffect(() => {
    console.log('🔵 [BankDetail] Employees state changed:', employees.length, 'employees')
    if (employees.length > 0) {
      console.log('🔵 [BankDetail] First employee in state:', employees[0])
    }
  }, [employees])

  const handleFieldChange = (field: keyof Bank, value: any) => {
    setBank((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError('Please select a branch first')
      return
    }

    if (!bank.empId || bank.empId === 0) {
      await showValidationError('Please select an employee')
      return
    }

    if (!bank.bankName || bank.bankName.trim() === '') {
      await showValidationError('Please enter bank name')
      return
    }

    if (!bank.accountTitle || bank.accountTitle.trim() === '') {
      await showValidationError('Please enter account title')
      return
    }

    if (!bank.accountNo || bank.accountNo.trim() === '') {
      await showValidationError('Please enter account number')
      return
    }

    try {
      setSaving(true)
      showLoading(
        isEditMode ? 'Updating Bank...' : 'Saving Bank...',
        'Please wait while we process your request'
      )

      const bankToSave: Bank = {
        id: bank.id ?? (isEditMode && id ? Number(id) : 0),
        empId: bank.empId,
        bankName: bank.bankName.trim(),
        accountTitle: bank.accountTitle.trim(),
        accountNo: String(bank.accountNo ?? '').trim(),
        iban: String(bank.iban ?? '').trim(),
        swiftCode: String(bank.swiftCode ?? '').trim(),
        note: String(bank.note ?? '').trim(),
        branchId: branchId,
      }

      if (isEditMode) {
        await bankService.updateBank(bankToSave)
      } else {
        await bankService.saveBank(bankToSave)
      }

      closeSwal()
      await showSaveSuccess(
        isEditMode,
        isEditMode ? 'Bank updated successfully' : 'Bank created successfully'
      )

      backToList()
    } catch (error: any) {
      console.error('Failed to save bank', error)
      closeSwal()
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  if (loading && !bank.empId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('banks.title')}
        description={isEditMode ? t('banks.updateBankDetails') : t('banks.createNewBankAccount')}
      />

      {/* Bank Form */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Emp Code */}
            <div className="space-y-2">
              <Label>{t('banks.labelEmpCode', { defaultValue: 'Emp #' })}</Label>
              <Input readOnly value={(selectedEmployee?.empCode ?? '').trim() || '—'} className="bg-muted" aria-readonly />
            </div>
            {/* Employee Dropdown */}
            <div className="space-y-2">
              <Label>
                {t('banks.employee')} <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                options={employeeOptions}
                value={bank.empId ? String(bank.empId) : ''}
                onValueChange={(value) => handleFieldChange('empId', Number(value))}
                placeholder={t('banks.selectEmployee')}
                searchPlaceholder={t('banks.searchEmployees')}
                emptyMessage={t('banks.noEmployeesFound')}
                disabled={saving || isEditMode || listEmpId > 0}
              />
            </div>

            {/* Bank Name */}
            <div className="space-y-2">
              <Label>
                {t('banks.bankName')} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={bank.bankName}
                onChange={(e) => handleFieldChange('bankName', e.target.value)}
                placeholder={t('banks.bankName')}
                disabled={saving}
              />
            </div>

            {/* Account Title */}
            <div className="space-y-2">
              <Label>
                {t('banks.accountTitle')} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={bank.accountTitle}
                onChange={(e) => handleFieldChange('accountTitle', e.target.value)}
                placeholder={t('banks.accountTitle')}
                disabled={saving}
              />
            </div>

            {/* Account No */}
            <div className="space-y-2">
              <Label>
                {t('banks.accountNo')} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={bank.accountNo}
                onChange={(e) => handleFieldChange('accountNo', e.target.value)}
                placeholder={t('banks.accountNo')}
                disabled={saving}
              />
            </div>

            {/* Iban */}
            <div className="space-y-2">
              <Label>
                {t('banks.iban')} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={bank.iban}
                onChange={(e) => handleFieldChange('iban', e.target.value)}
                placeholder={t('banks.iban')}
                disabled={saving}
              />
            </div>

            {/* Swift Code */}
            <div className="space-y-2">
              <Label>
                {t('banks.swiftCode')} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={bank.swiftCode}
                onChange={(e) => handleFieldChange('swiftCode', e.target.value)}
                placeholder={t('banks.swiftCode')}
                disabled={saving}
              />
            </div>

            {/* Note */}
            <div className="space-y-2 col-span-full">
              <Label>
                {t('banks.note')} <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={bank.note}
                onChange={(e) => handleFieldChange('note', e.target.value)}
                placeholder={t('banks.note')}
                disabled={saving}
                className="min-h-[100px]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => backToList()}
          className="w-full sm:w-auto"
          disabled={saving}
        >
          {t('banks.return')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !branchId}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isEditMode ? t('banks.updating') : t('banks.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('common.update') : t('banks.save')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
